import { DateRangePickerProps } from '@amzn/awsui-components-console/date-range-picker';

import {
  createSavedSearchStore,
  SavedSearchEntry,
} from 'src/common/savedSearches';

/**
 * Saved searches for the Track Translations page.
 *
 * The read/write/evict/notify mechanics live in the shared store — see
 * `src/common/savedSearches/savedSearchStore.ts`, which also documents the
 * TEMPORARY localStorage persistence and the users-DynamoDB-table plan. Only
 * what is specific to this page is here: the storage key, the shape of the
 * saved state and how it is validated, and the label wording.
 */

const SAVED_TRACK_SEARCHES_KEY = 'LangHub-savedTrackSearches';

/** Fired after a write so bars in the same tab refresh. */
export const SAVED_TRACK_SEARCHES_CHANGED =
  'langhub-saved-track-searches-changed';

/** The filter selection a saved search restores. */
export interface SavedTrackSearchState {
  /** Namespace ID. Always present — the page cannot query without one. */
  namespace: string;
  /** String group ID, or '' meaning all groups. */
  stringGroup: string;
  /** Date range, or null when the user cleared it. */
  dateRange: DateRangePickerProps.Value | null;
}

export type SavedTrackSearch = SavedSearchEntry<SavedTrackSearchState>;

/**
 * Whether a raw stored date range is usable. Absent is valid (the user may have
 * cleared the range); a malformed object is not, and drops the whole entry
 * rather than restoring a filter the DateRangePicker cannot render.
 */
function isStoredDateRangeValid(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value !== 'object') return false;

  const range = value as Record<string, unknown>;
  if (range.type === 'relative') {
    return typeof range.amount === 'number' && typeof range.unit === 'string';
  }
  if (range.type === 'absolute') {
    return (
      typeof range.startDate === 'string' && typeof range.endDate === 'string'
    );
  }
  return false;
}

/** Validates a raw stored state, returning null when it cannot be applied. */
function parseState(raw: unknown): SavedTrackSearchState | null {
  if (!raw || typeof raw !== 'object') return null;

  const state = raw as Record<string, unknown>;

  // A saved search with no namespace could never be applied — the Track queries
  // require one — so treat it as unusable rather than restoring a dead filter.
  if (typeof state.namespace !== 'string' || state.namespace.length === 0) {
    return null;
  }
  if (typeof state.stringGroup !== 'string') return null;
  if (!isStoredDateRangeValid(state.dateRange)) return null;

  return {
    namespace: state.namespace,
    stringGroup: state.stringGroup,
    dateRange:
      (state.dateRange as DateRangePickerProps.Value | null | undefined) ??
      null,
  };
}

/** Human-readable summary of a date range, for auto-generated labels. */
function describeDateRange(
  dateRange: DateRangePickerProps.Value | null,
): string | null {
  if (!dateRange) return null;
  if (dateRange.type === 'relative') {
    const plural = dateRange.amount === 1 ? '' : 's';
    return `Last ${dateRange.amount} ${dateRange.unit}${plural}`;
  }
  return `${dateRange.startDate} to ${dateRange.endDate}`;
}

/** Suggested name for a saved search, used to pre-fill the naming dialog. */
export function generateTrackSearchLabel(state: SavedTrackSearchState): string {
  const parts: string[] = [state.namespace];
  parts.push(state.stringGroup || 'All groups');

  const range = describeDateRange(state.dateRange);
  if (range) parts.push(range);

  return parts.join(' · ').slice(0, 50);
}

const store = createSavedSearchStore<SavedTrackSearchState>({
  storageKey: SAVED_TRACK_SEARCHES_KEY,
  changedEvent: SAVED_TRACK_SEARCHES_CHANGED,
  parseState,
  generateLabel: generateTrackSearchLabel,
});

/** Kept in sync with the Save Search info tooltip copy in TrackFilters. */
export const MAX_SAVED_TRACK_SEARCHES = store.maxEntries;

export function getSavedTrackSearches(): SavedTrackSearch[] {
  return store.getAll();
}

export function saveTrackSearch(
  state: SavedTrackSearchState,
  name?: string,
): void {
  store.save(state, name);
}

export function removeSavedTrackSearch(id: string): void {
  store.remove(id);
}
